<template lang="pug">
#draft-message-list(v-cloak)
  DraftMessageTable(
    v-model="items"
    :fields="tableFields"
    :totalRows.sync="rows"
    :currentPage="currentPage"
    :perPage="perPage"
    :isLoading="isLoading"
    @onEdit="onEdit"
    @onDelete="onDelete"
    :waiterFamilyMap="waiterFamilyMap"
  )
  .table-options.d-flex.flex-row.justify-content-end.align-items-center
    // ページあたりの行数
    span.table-option-text 1ページの行数:
    .per-page
      b-form-select(
        id="per-page-select"
        v-model="perPage"
        :options="pageOptions"
        size="md"
        @change="selectPerPage"
      )
    // ページネーション
    b-pagination.m-0(
      v-model="currentPage"
      :total-rows="rows"
      :per-page="perPage"
      :disabled="isLoading"
      @change="selectPage"
      hide-goto-end-buttons
    )

  OkCancelModal(v-model="showDeleteModal" :title="deleteMessage" @ok="doDelete")
  OkModal(v-model="showDeleteSuccessModal" title="削除に成功しました。" @ok="createView")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Watch, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { errorHandle } from "@/errorHandler";
import { PcUrl } from "@/commons/constants/PcUrl";

import Setting from "../../../../public/setting.json";
import DraftMessagesService, { DraftMessageSendTargetType, DraftMessagesGetModel } from "@/commons/services/DraftMessages";
import WaiterFamiliesService, { WaiterFamiliesGetModel } from "@/commons/services/WaiterFamilies";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import DraftMessageTable, { DraftMessageTableField, DraftMessageTableItem } from "@/pc/containers/sendMessages/DraftMessageTable.vue";

/**
 * ### メッセージ下書き一覧コンポーネント
 */
@Component({
  components: {
    OkCancelModal,
    OkModal,
    PageLoading,
    DraftMessageTable
  }
})
export default class DraftMessageListTab extends Vue {
  /** メッセージ下書きサービス */
  @Inject("DraftMessagesService") draftMessagesService!: DraftMessagesService;
  /** 待機者家族サービス */
  @Inject("WaiterFamiliesService") waiterFamiliesService!: WaiterFamiliesService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 削除確認モーダル表示可否 */
  showDeleteModal: boolean = false;
  /** 削除完了モーダル表示可否 */
  showDeleteSuccessModal: boolean = false;

  /** メッセージ下書きテーブル列名 */
  tableFields: Array<DraftMessageTableField> = [];
  /** メッセージ下書きテーブル各行の情報 */
  items: Array<DraftMessageTableItem> = [];
  /** メッセージ下書きテーブルで使用する家族名変換用MAP */
  waiterFamilyMap: Map<string, WaiterFamiliesGetModel> = new Map<string, WaiterFamiliesGetModel>();
  /** 読込み中か否か */
  isLoading: boolean = true;
  /** 削除対象の利用者ID */
  deleteTargetId: number = 0;
  /** 削除モーダルに表示するメッセージ */
  deleteMessage: string = "";

  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** ページングの全体行数 */
  rows: number = 0;
  /** 1ページに表示する行数 */
  perPage: number = Number(Setting.draftMessage.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.draftMessage.pageOptions;

  /**
   * ### 1ページあたりの表示行数の変更イベント
   *
   * - 変更をクッキーに持たせる
   */
  @Watch("perPage")
  public changePerPage(perPage: number): void {
    this.setCookie(perPage);

    return;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  public setCookie(perPage: number): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `draft-message-list-per-page=${perPage}; max-age=${saveSecond};`;

    return;
  }

  /**
   * ### vue lifecycle mounted
   *
   * - 画面表示情報の作成を行う
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const tableFields: Array<DraftMessageTableField> = this.createTableFields();
    this.tableFields = tableFields;
    // 1ページあたりの表示行数はCookieから取得する。取得できない場合はデフォルト値を使用する
    const perPage: number | null = this.loadCookie();
    this.perPage = perPage ? perPage : Number(Setting.draftMessage.perPage);

    await this.initialize();

    return;
  }

  /**
   * ### メッセージ下書きの列名を作成する
   */
  public createTableFields(): Array<DraftMessageTableField> {
    let fields: Array<DraftMessageTableField> = [];

    fields.push({ key: "sendTarget", label: "送信先", sortable: false, sortDirection: "desc", class: "send-target text-center" });
    fields.push({ key: "targets", label: "宛先", sortable: false, sortDirection: "desc", class: "targets" });
    fields.push({ key: "title", label: "タイトル", sortable: false, sortDirection: "desc", class: "title" });
    fields.push({ key: "writtenBy", label: "記載者", sortable: false, sortDirection: "desc", class: "written-by" });
    fields.push({ key: "operation", label: "操作", sortable: false, sortDirection: "desc", class: "operation text-center" });

    return fields;
  }

  /**
   * ### Cookieを見て、値が保持されている場合はCookieの値を返す
   */
  public loadCookie(): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      // cookieに値が格納されていないとundefinedが格納されているが、実際にはゴミデータが格納されているのでisNaN()でチェックが必要
      const perPage: number = cookieMap["draft-message-list-per-page"];

      return isNaN(perPage) ? null : perPage;
    }

    return null;
  }

  /**
   * ### 画面初期化
   */
  public async initialize(): Promise<void> {
    this.isLoading = true;

    this.items = [];
    this.currentPage = 1;

    await this.createView();

    this.isLoading = false;

    return;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    // 全体の行数を非同期で取得する
    this.setRows();

    // メッセージ下書きテーブルに渡すMAPを作成する
    const waiterFamilies: Array<WaiterFamiliesGetModel> = await this.getWaiterFamilies();
    this.waiterFamilyMap = this.createWaiterFamilyMap(waiterFamilies);

    // メッセージ下書きテーブルに渡す各プロパティを作成する
    const draftMessages: Array<DraftMessagesGetModel> = await this.getDraftMessages();
    const filterdDraftMessages: Array<DraftMessagesGetModel> = this.filteringByType(draftMessages);
    const items: Array<DraftMessageTableItem> = this.createTableItems(filterdDraftMessages);
    this.items = items;

    return;
  }

  /**
   * ### ページング用の全件数を設定する
   */
  public async setRows() {
    try {
      const draftMessages: Array<DraftMessagesGetModel> = await this.getDraftMessages();
      const filterdDraftMessages: Array<DraftMessagesGetModel> = this.filteringByType(draftMessages);
      this.rows = filterdDraftMessages.length;
    } catch (error) {
      // 本処理はawaitされない非同期処理なので、共通エラーハンドラーが例外をキャッチしない
      // 例外発生時は自分でエラーハンドラーを呼ぶ
      errorHandle(error, this);
    }

    return;
  }

  /**
   * ### 施設にひもづく家族を取得する
   */
  public async getWaiterFamilies(): Promise<Array<WaiterFamiliesGetModel>> {
    const response: Array<WaiterFamiliesGetModel> = await this.waiterFamiliesService.getsInFacility(userStore);
    return response;
  }

  /**
   * ### 家族名変換用MAPを作成する
   *
   * - key：ソートキー
   * - value：家族名
   */
  public createWaiterFamilyMap(families: Array<WaiterFamiliesGetModel>): Map<string, WaiterFamiliesGetModel> {
    const map: Map<string, WaiterFamiliesGetModel> = new Map<string, WaiterFamiliesGetModel>();
    for (const family of families) {
      map.set(family.residentsSortKey, family);
    }
    return map;
  }

  /**
   * ### メッセージ下書きを取得する
   */
  public async getDraftMessages(): Promise<Array<DraftMessagesGetModel>> {
    const draftMessages: Array<DraftMessagesGetModel> = await this.draftMessagesService.gets(userStore);
    return draftMessages;
  }

  /**
   * ### 下書きメッセージを種別でフィルタリングする
   */
  public filteringByType(draftMessages: Array<DraftMessagesGetModel>): Array<DraftMessagesGetModel> {
    return draftMessages.concat().filter((message) => {
      switch (message.type) {
        case DraftMessageSendTargetType.ALL_FAMILY:
        case DraftMessageSendTargetType.SELECTED_FAMILY:
        case DraftMessageSendTargetType.SELECTED_OTHER_USER_GROUP:
        case DraftMessageSendTargetType.SEND_MESSGE_GROUP: {
          return false;
        }
        case DraftMessageSendTargetType.ALL_WAITER:
        case DraftMessageSendTargetType.SELECTED_WAITER: {
          return true;
        }
        default: {
          return false;
        }
      }
    });
  }

  /**
   * ### メッセージ下書きの行を作成する
   */
  public createTableItems(draftMessages: Array<DraftMessagesGetModel>): Array<DraftMessageTableItem> {
    return draftMessages;
  }

  /**
   * ### 1ページあたりの表示行数変更イベント
   *
   * - 1ページあたりの表示行数を更新
   * - ページあたりの表示行数が変わるので表の情報を作り直す
   */
  public async selectPerPage(perPage: number): Promise<void> {
    this.perPage = perPage;
    this.currentPage = 1;
    return;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;
    return;
  }

  /**
   * ### 新規送信タブに選択されたメッセージ下書きを復元する
   *
   * - 親コンポーネントにメッセージ下書きを渡して親コンポーネント経由で新規送信タブに内容を反映させる
   */
  public onEdit(draftMessage: DraftMessageTableItem): void {
    this.$emit("restoreDraftMessage", draftMessage);
    return;
  }

  /**
   * ### 削除確認モーダルを表示する
   *
   * - 削除メッセージは動的に作成する
   */
  public onDelete(draftMessage: DraftMessageTableItem): void {
    const tiltle: string = draftMessage.title != null ? draftMessage.title : "";
    this.deleteMessage = `「${tiltle}」の下書きを削除しますか？`;
    this.showDeleteModal = true;

    this.deleteTargetId = draftMessage.id;

    return;
  }

  /**
   * ### 下書きメッセージを削除する
   */
  public async doDelete(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "削除中...";

    await this.draftMessagesService.delete(userStore, this.deleteTargetId);

    // itemsから削除した利用者を削除するだけで再取得リクエストはしない
    this.items = this.items.concat().filter((item: DraftMessageTableItem) => {
      if (item.id == this.deleteTargetId) {
        return false;
      } else {
        return true;
      }
    });
    this.rows--;

    // 削除成功ダイアログを表示する
    this.showDeleteSuccessModal = true;
    this.isBusy = false;

    return;
  }

  /**
   * ### 画面の作り直し
   *
   * - 親コンポーネントから呼び出される
   */
  public async refresh(): Promise<void> {
    await this.initialize();
    return;
  }

  /**
   * ### 画面の作り直し
   */
  public async refreshByNoLoading(): Promise<void> {
    try {
      await this.createView();
    } catch (error) {
      // 処理中に画面遷移が発生した場合、エラーと見なさないためURLチェックする
      if (this.$route.fullPath != PcUrl.SendMessage) {
        return;
      } else {
        throw error;
      }
    }
    return;
  }
}
</script>

<style lang="scss" scoped>
// 親要素からネストしてスタイルを指定する
#draft-message-list {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;
}
</style>
